import type { TypeId } from '@unipile/db';
import type { ProviderAuthenticationCheckpointInput, ProviderAuthenticationIntentResponse } from '@unipile/types';
import { Controller, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useSolveCheckpoint } from '../../../features/accounts/api/solve-checkpoint';
import { Button } from '../../shadcn/Button';
import { Field, FieldGroup, FieldLabel } from '../../shadcn/Field';
import { Input } from '../../shadcn/Input';
import { Spinner } from '../../shadcn/Spinner';
import { WizardStepContainer } from '../containers/WizardStepContainer';

/**
 * A checkpoint that requires a phone number to be registered.
 */
export function PhoneRegisterCheckpoint({
  onSuccess,
  onError,
  intent_id,
  title,
  description,
}: {
  onSuccess: (response: ProviderAuthenticationIntentResponse) => void;
  onError: (error: unknown) => void;
  intent_id: TypeId<'account'>;
  title: string;
  description: string;
}) {
  const { mutate, isPending } = useSolveCheckpoint({
    mutationConfig: {
      onSuccess,
      onError,
    },
  });
  const form = useForm<ProviderAuthenticationCheckpointInput>();
  const { t } = useTranslation();
  const onSubmit = (data: ProviderAuthenticationCheckpointInput) => {
    mutate({ data: { ...data, intent_id } });
  };

  return (
    <WizardStepContainer title={title} description={description}>
      <form onSubmit={form.handleSubmit(onSubmit)}>
        <FieldGroup>
          <Controller
            control={form.control}
            name="code"
            rules={{ required: true }}
            render={({ field }) => (
              <Field>
                <FieldLabel>{t('PhoneRegisterCheckpoint.phoneNumber')}</FieldLabel>
                <Input type="tel" {...field} />
              </Field>
            )}
          />

          <Button className="mt-8 w-full" type="submit" disabled={isPending}>
            {isPending && <Spinner />}
            {t('Common.next')}
          </Button>
        </FieldGroup>
      </form>
    </WizardStepContainer>
  );
}
